import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

interface EnrolledCourse {
  id: string;
  course_id: string;
  enrolled_at: string;
  expire_date: string;
  status: string;
  classrooms: Array<{ id: string; name: string }>;
}

interface UseEnrolledCoursesReturn {
  enrolledCourses: EnrolledCourse[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

export const useEnrolledCourses = (userId: string | undefined): UseEnrolledCoursesReturn => {
  console.log('[useEnrolledCourses] Hook called with userId:', userId, 'at:', new Date().toISOString());
  
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayCount, setDisplayCount] = useState(6);
  
  // Memoize cache key to prevent recreation on every render
  const cacheKey = useMemo(() => `enrolled_courses_${userId}`, [userId]);
  const TTL_MS = 10 * 60 * 1000; // 10 minutes
  const initializedFromCacheRef = useRef(false);
  const lastFetchTimeRef = useRef<number>(0);
  const isFetchingRef = useRef(false);

  // Try to load from cache on mount for instant display
  useEffect(() => {
    if (!userId) return;
    
    try {
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw) as { data: EnrolledCourse[]; updatedAt: number };
        if (Array.isArray(cached.data) && cached.data.length > 0) {
          setEnrolledCourses(cached.data);
          initializedFromCacheRef.current = true;
          lastFetchTimeRef.current = cached.updatedAt;
          
          // Check if cache is fresh
          if (cached.updatedAt && Date.now() - cached.updatedAt < TTL_MS) {
            console.log('[useEnrolledCourses] Using fresh cache, skipping fetch');
            return; // Skip fetch if cache is fresh
          }
        }
      }
    } catch (error) {
      console.warn('[useEnrolledCourses] Cache read error:', error);
    }
  }, [userId, cacheKey]);

  // Store the fetch function in a ref to prevent recreation
  const fetchEnrolledCoursesRef = useRef<typeof fetchEnrolledCourses>();
  
  const fetchEnrolledCourses = useCallback(async (showLoader = true, forceRefresh = false) => {
    if (!userId) return;
    
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current && !forceRefresh) {
      console.log('[useEnrolledCourses] Already fetching, skipping duplicate request');
      return;
    }
    
    // Check if we need to fetch (cache is fresh and not forcing refresh)
    if (!forceRefresh && lastFetchTimeRef.current && Date.now() - lastFetchTimeRef.current < TTL_MS) {
      console.log('[useEnrolledCourses] Cache is fresh, skipping fetch');
      return;
    }
    
    isFetchingRef.current = true;
    
    if (showLoader) {
      setLoading(true);
    }
    setError(null);

    try {
      console.log('[useEnrolledCourses] Fetching enrolled courses for user:', userId);
      
      // Fetch from users > currentUserId > Courses subcollection
      const userCoursesRef = collection(db, 'users', userId, 'Courses');
      const querySnapshot = await getDocs(userCoursesRef);
      
      const enrolled: EnrolledCourse[] = [];
      const courseIds = querySnapshot.docs.map(doc => doc.data().course_id).filter(Boolean);
      
      console.log('[useEnrolledCourses] Found enrolled course IDs:', courseIds);
      
      // Only fetch classroom data for the first few courses to improve performance
      const coursesToFetchClassrooms = courseIds.slice(0, 3); // Limit to first 3 courses
      
      // Batch fetch classroom data for all courses at once
      const classroomPromises = coursesToFetchClassrooms.map(async (courseId) => {
        try {
          const classroomRef = collection(db, 'courses', courseId, 'Classrooms');
          const classroomSnapshot = await getDocs(classroomRef);
          
          if (!classroomSnapshot.empty) {
            const classroomData: { id: string; name: string }[] = [];
            
            classroomSnapshot.forEach((cDoc) => {
              const d = cDoc.data() as any;
              
              // Extract classroom names directly from the document
              if (typeof d.classroomName === 'string' && d.classroomName.trim()) {
                classroomData.push({ id: cDoc.id, name: d.classroomName });
              }
              
              // Handle classroomId arrays
              const raw = d.classroomId;
              if (Array.isArray(raw)) {
                raw.forEach((item) => {
                  if (typeof item === 'string') {
                    classroomData.push({ id: item, name: item });
                  } else if (item && typeof item === 'object' && item.classroomName) {
                    classroomData.push({ 
                      id: item.id || item.classroomId || cDoc.id, 
                      name: item.classroomName 
                    });
                  }
                });
              }
            });
            
            // Deduplicate classrooms
            const uniqueClassrooms = Array.from(
              new Map(classroomData.map(c => [c.id, c])).values()
            );
            
            return { courseId, classrooms: uniqueClassrooms };
          }
        } catch (error) {
          console.warn('[useEnrolledCourses] Error fetching classrooms for course:', courseId, error);
          return { courseId, classrooms: [] };
        }
        return { courseId, classrooms: [] };
      });
      
      // Wait for all classroom data to be fetched
      const classroomResults = await Promise.all(classroomPromises);
      const courseClassroomMap = new Map(
        classroomResults.map(result => [result.courseId, result.classrooms])
      );
      
      // Process enrolled courses
      for (const docSnapshot of querySnapshot.docs) {
        const courseData = docSnapshot.data();
        const courseId = courseData.course_id;
        
        // Get classroom data from the map (or empty array if not fetched)
        const classroomData = courseClassroomMap.get(courseId) || [];
        
        enrolled.push({
          id: docSnapshot.id,
          course_id: courseId,
          enrolled_at: courseData.enrolled_at,
          expire_date: courseData.expire_date,
          status: courseData.status,
          classrooms: classroomData,
        });
      }
      
      console.log('[useEnrolledCourses] Processed enrolled courses:', enrolled.length);
      setEnrolledCourses(enrolled);
      
      // Update cache and fetch time
      const now = Date.now();
      lastFetchTimeRef.current = now;
      
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ 
          data: enrolled, 
          updatedAt: now
        }));
      } catch (error) {
        console.warn('[useEnrolledCourses] Cache write error:', error);
      }
      
    } catch (error) {
      console.error('[useEnrolledCourses] Error fetching enrolled courses:', error);
      setError('Failed to load enrolled courses');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [userId, cacheKey]);

  // Store the function in ref to prevent recreation
  fetchEnrolledCoursesRef.current = fetchEnrolledCourses;

  const refresh = useCallback(async () => {
    await fetchEnrolledCourses(true, true); // Force refresh
  }, [fetchEnrolledCourses]);

  const loadMore = useCallback(async () => {
    setDisplayCount(prev => prev + 6);
  }, []);

  // Fetch enrolled courses when user is available (only once)
  useEffect(() => {
    console.log('[useEnrolledCourses] useEffect triggered:', { 
      userId, 
      initializedFromCache: initializedFromCacheRef.current, 
      isFetching: isFetchingRef.current 
    });
    
    if (userId && !initializedFromCacheRef.current && !isFetchingRef.current) {
      console.log('[useEnrolledCourses] Initiating fetch');
      // Use the ref to avoid dependency issues
      if (fetchEnrolledCoursesRef.current) {
        fetchEnrolledCoursesRef.current(false, false);
      }
    } else {
      console.log('[useEnrolledCourses] Skipping fetch - conditions not met:', {
        userId: !!userId,
        initializedFromCache: initializedFromCacheRef.current,
        isFetching: isFetchingRef.current
      });
    }
  }, [userId]); // Only depend on userId

  // Memoize hasMore to prevent unnecessary re-renders
  const hasMore = useMemo(() => {
    return enrolledCourses.length > displayCount;
  }, [enrolledCourses.length, displayCount]);

  return {
    enrolledCourses,
    loading,
    error,
    refresh,
    hasMore,
    loadMore,
  };
};
